import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';

import IconVipps from '../icons/IconVipps';

import css from './SocialAuthButtons.module.css';

const FacebookLogo = () => (
  <svg
    className={css.brandIcon}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M20 10C20 4.477 15.523 0 10 0S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.879V12.89H5.898V10h2.54V7.797c0-2.506 1.492-3.89 3.776-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.772-1.63 1.563V10h2.773l-.443 2.89h-2.33V19.88C16.343 19.128 20 14.991 20 10Z"
      fill="#1877F2"
    />
  </svg>
);

const GoogleLogo = () => (
  <svg
    className={css.brandIcon}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 0 1-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35Z"
      fill="#4285F4"
    />
    <path
      d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.896.6-2.041.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0 0 10 20Z"
      fill="#34A853"
    />
    <path
      d="M4.405 11.9c-.2-.6-.314-1.241-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 0 0 0 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59Z"
      fill="#FBBC05"
    />
    <path
      d="M10 3.977c1.469 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0A9.996 9.996 0 0 0 1.064 5.51l3.34 2.59C5.191 5.736 7.396 3.977 10 3.977Z"
      fill="#EA4335"
    />
  </svg>
);

/**
 * Vipps + Google + Facebook social-auth button stack used on both the
 * welcome / signup page and the login page.
 *
 * - Vipps / Google / Facebook buttons render only when the matching env
 *   client id is set, and their click handlers are passed in by the
 *   caller (the AuthenticationPage builds them using the apiBaseUrl +
 *   idp routes).
 * - `pill`: white rounded options side by side (the campaign page), with any
 *   `children` (e.g. an email link styled with `pillClassName`) after them.
 *
 * @component
 * @param {Object} props
 * @param {string} props.vippsLabelId – translation key for the Vipps label
 * @param {string} props.googleLabelId – translation key for the Google label
 * @param {string} props.facebookLabelId – translation key for the Facebook label
 * @param {boolean} [props.showVippsLogin]
 * @param {boolean} [props.showFacebookLogin]
 * @param {boolean} [props.showGoogleLogin]
 * @param {Function} [props.onAuthWithVipps]
 * @param {Function} [props.onAuthWithFacebook]
 * @param {Function} [props.onAuthWithGoogle]
 * @param {boolean} [props.pill] - side-by-side pills instead of a stack
 * @param {ReactNode} [props.children] - extra options after the buttons
 * @returns {JSX.Element}
 */
const SocialAuthButtons = props => {
  const {
    vippsLabelId,
    googleLabelId,
    facebookLabelId,
    showVippsLogin,
    showFacebookLogin,
    showGoogleLogin,
    onAuthWithVipps,
    onAuthWithFacebook,
    onAuthWithGoogle,
    pill = false,
    children = null,
  } = props;

  const outlinedClassName = pill ? css.pillButton : css.outlinedButton;

  return (
    <div className={pill ? css.rootPill : css.root}>
      {showVippsLogin ? (
        <button
          type="button"
          className={pill ? css.pillButton : css.vippsButton}
          onClick={onAuthWithVipps}
        >
          <IconVipps
            className={classNames(css.vippsIcon, { [css.pillIcon]: pill })}
            inverted={pill}
          />
          <span className={css.label}>
            <FormattedMessage id={vippsLabelId} />
          </span>
        </button>
      ) : null}

      {showGoogleLogin ? (
        <button type="button" className={outlinedClassName} onClick={onAuthWithGoogle}>
          <GoogleLogo />
          <span className={css.label}>
            <FormattedMessage id={googleLabelId} />
          </span>
        </button>
      ) : null}

      {showFacebookLogin ? (
        <button type="button" className={outlinedClassName} onClick={onAuthWithFacebook}>
          <FacebookLogo />
          <span className={css.label}>
            <FormattedMessage id={facebookLabelId} />
          </span>
        </button>
      ) : null}
      {children}
    </div>
  );
};

export default SocialAuthButtons;
